import React, { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  TAgetComposedPipelineFlow,
  TAgetActiveRunpodPods,
  TAterminateRunpodPod,
  TAterminateAllRunpodPods,
  HashtagPipelineFlowData,
  RunpodPod,
} from "../../../services/hashtagPipelineAPI"

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "gray"
  percent?: number
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, color = "blue", percent }) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-green-50 border-green-200 text-green-800",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
    red: "bg-red-50 border-red-200 text-red-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    gray: "bg-gray-50 border-gray-200 text-gray-800",
  }

  return (
    <div className={`p-4 rounded-lg border-2 ${colorClasses[color]}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-2xl font-bold mt-1">
        {typeof value === "number" ? value.toLocaleString("tr-TR") : value}
      </div>
      {percent !== undefined && (
        <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {subtitle && <div className="text-xs mt-1 opacity-70">{subtitle}</div>}
    </div>
  )
}


const HashtagPipeline: React.FC = () => {
  const navigate = useNavigate()

  const [pipelineFlow, setPipelineFlow] = useState<HashtagPipelineFlowData | null>(null)
  const [pods, setPods] = useState<RunpodPod[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPods, setIsLoadingPods] = useState(false)
  const [error, setError] = useState("")
  const [terminatingPod, setTerminatingPod] = useState<string | null>(null)
  const [terminatingAll, setTerminatingAll] = useState(false)

  const fetchPipelineFlow = useCallback(async () => {
    try {
      const data = await TAgetComposedPipelineFlow()
      setPipelineFlow(data)
      setError("")
    } catch (err: unknown) {
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 401
      ) {
        navigate("/login")
        return
      }
      setError("Pipeline verileri yüklenirken hata oluştu")
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const fetchPods = useCallback(async () => {
    setIsLoadingPods(true)
    try {
      const data = await TAgetActiveRunpodPods()
      setPods(data.pods)
    } catch (err) {
      console.error("Pods error:", err)
    } finally {
      setIsLoadingPods(false)
    }
  }, [])

  useEffect(() => {
    fetchPipelineFlow()
    fetchPods()

    // Her dakika otomatik güncelle
    const interval = setInterval(() => {
      fetchPipelineFlow()
      fetchPods()
    }, 60000)

    return () => clearInterval(interval)
  }, [fetchPipelineFlow, fetchPods])

  const handleTerminatePod = async (podId: string) => {
    if (!window.confirm(`${podId} pod'unu terminate etmek istediğinize emin misiniz?`)) {
      return
    }

    setTerminatingPod(podId)
    try {
      await TAterminateRunpodPod(podId)
      setPods((prev) => prev.filter((p) => p.id !== podId))
    } catch (err) {
      console.error("Terminate error:", err)
      alert("Pod terminate edilirken hata oluştu")
    } finally {
      setTerminatingPod(null)
    }
  }

  const handleTerminateAll = async () => {
    if (!window.confirm(`Tüm ${pods.length} pod'u terminate etmek istediğinize emin misiniz?`)) {
      return
    }

    setTerminatingAll(true)
    try {
      await TAterminateAllRunpodPods()
      setPods([])
      fetchPipelineFlow()
    } catch (err) {
      console.error("Terminate all error:", err)
      alert("Podlar terminate edilirken hata oluştu")
    } finally {
      setTerminatingAll(false)
    }
  }

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}s ${minutes}dk`
  }

  const getPercent = (completed: number, total: number) =>
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0

  const fmt = (v: number | string | undefined | null) =>
    typeof v === "number" ? v.toLocaleString("tr-TR") : v ?? "-"

  const renderStatusBadge = (status: "completed" | "in_progress" | "pending") => {
    const config = {
      completed: { label: "Tamam", icon: "✅", cls: "bg-green-100 text-green-800" },
      in_progress: { label: "Devam", icon: "⏳", cls: "bg-yellow-100 text-yellow-800" },
      pending: { label: "Bekliyor", icon: "🔴", cls: "bg-red-100 text-red-800" },
    }
    const c = config[status]
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${c.cls}`}>
        {c.icon} {c.label}
      </span>
    )
  }

  const buildStatus = (total: number, waiting: number): "completed" | "in_progress" | "pending" => {
    if (total === 0) {
      return "pending"
    }
    return waiting === 0 ? "completed" : "in_progress"
  }

  const pipelineTableData: {
    num: number
    title: string
    desc: string
    status: "completed" | "in_progress" | "pending"
    total: number | string
    completed: number | string
    waiting: number | string
    waitingExtra?: string
    percent: number
  }[] = pipelineFlow
    ? [
        {
          num: 1,
          title: "Hashtag Managers",
          desc: "Job durumları ve aktiflik",
          status: buildStatus(pipelineFlow.hashtagManagers.total, pipelineFlow.hashtagManagers.active),
          total: pipelineFlow.hashtagManagers.total,
          completed: pipelineFlow.hashtagManagers.completed,
          waiting: pipelineFlow.hashtagManagers.active,
          percent: getPercent(
            pipelineFlow.hashtagManagers.completed,
            pipelineFlow.hashtagManagers.total,
          ),
        },
        {
          num: 2,
          title: "Hashtag Post Kaydetme",
          desc: "Hashtag postlarının kayıt durumu",
          status: buildStatus(
            pipelineFlow.hashtagPostCounts.totalHashtagPosts,
            pipelineFlow.hashtagPostCounts.unsavedHashtagPosts,
          ),
          total: pipelineFlow.hashtagPostCounts.totalHashtagPosts,
          completed: pipelineFlow.hashtagPostCounts.savedHashtagPosts,
          waiting: pipelineFlow.hashtagPostCounts.unsavedHashtagPosts,
          percent: getPercent(
            pipelineFlow.hashtagPostCounts.savedHashtagPosts,
            pipelineFlow.hashtagPostCounts.totalHashtagPosts,
          ),
        },
        {
          num: 3,
          title: "Hashtag RunPod Analizi",
          desc: "Hashtag postları için RunPod analizi",
          status: buildStatus(
            pipelineFlow.hashtagPostCounts.hashtagPostsRunpodAnalyzed +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis,
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis,
          ),
          total:
            pipelineFlow.hashtagPostCounts.hashtagPostsRunpodAnalyzed +
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis,
          completed: pipelineFlow.hashtagPostCounts.hashtagPostsRunpodAnalyzed,
          waiting: pipelineFlow.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis,
          percent: getPercent(
            pipelineFlow.hashtagPostCounts.hashtagPostsRunpodAnalyzed,
            pipelineFlow.hashtagPostCounts.hashtagPostsRunpodAnalyzed +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis,
          ),
        },
        {
          num: 4,
          title: "Hashtag GPT Analizi",
          desc: "Hashtag postları için GPT analizi",
          status: buildStatus(
            pipelineFlow.hashtagPostCounts.hashtagPostsGptAnalyzed +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingGptAnalysis,
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingGptAnalysis,
          ),
          total:
            pipelineFlow.hashtagPostCounts.hashtagPostsGptAnalyzed +
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingGptAnalysis,
          completed: pipelineFlow.hashtagPostCounts.hashtagPostsGptAnalyzed,
          waiting: pipelineFlow.hashtagPostCounts.hashtagPostsNeedingGptAnalysis,
          percent: getPercent(
            pipelineFlow.hashtagPostCounts.hashtagPostsGptAnalyzed,
            pipelineFlow.hashtagPostCounts.hashtagPostsGptAnalyzed +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingGptAnalysis,
          ),
        },
        {
          num: 5,
          title: "Product Embedding",
          desc: "Hashtag post product embedding kuyruğu",
          status: buildStatus(
            pipelineFlow.hashtagPostCounts.hashtagPostsProductEmbedding +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingProductEmbedding,
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingProductEmbedding,
          ),
          total:
            pipelineFlow.hashtagPostCounts.hashtagPostsProductEmbedding +
            pipelineFlow.hashtagPostCounts.hashtagPostsNeedingProductEmbedding,
          completed: pipelineFlow.hashtagPostCounts.hashtagPostsProductEmbedding,
          waiting: pipelineFlow.hashtagPostCounts.hashtagPostsNeedingProductEmbedding,
          percent: getPercent(
            pipelineFlow.hashtagPostCounts.hashtagPostsProductEmbedding,
            pipelineFlow.hashtagPostCounts.hashtagPostsProductEmbedding +
              pipelineFlow.hashtagPostCounts.hashtagPostsNeedingProductEmbedding,
          ),
        },
        {
          num: 6,
          title: "Pinecone Embedding",
          desc: "Pinecone embedding hazırlık ve işleme",
          status: buildStatus(
            pipelineFlow.hashtagPostCounts.hashtagPostsEligibleForPineconeEmbedding,
            pipelineFlow.hashtagPostCounts.hashtagPostsWaitingPineconeEmbedding,
          ),
          total: pipelineFlow.hashtagPostCounts.hashtagPostsEligibleForPineconeEmbedding,
          completed: pipelineFlow.hashtagPostCounts.hashtagPostsPineconeEmbedded,
          waiting: pipelineFlow.hashtagPostCounts.hashtagPostsWaitingPineconeEmbedding,
          percent: getPercent(
            pipelineFlow.hashtagPostCounts.hashtagPostsPineconeEmbedded,
            pipelineFlow.hashtagPostCounts.hashtagPostsEligibleForPineconeEmbedding,
          ),
        },
        {
          num: 7,
          title: "Influencer Post Kaydetme",
          desc: "Influencer postlarının kayıt durumu",
          status: buildStatus(
            pipelineFlow.influencers.totalInfluencerPosts,
            pipelineFlow.influencers.unsavedInfluencerPosts,
          ),
          total: pipelineFlow.influencers.totalInfluencerPosts,
          completed: pipelineFlow.influencers.savedInfluencerPosts,
          waiting: pipelineFlow.influencers.unsavedInfluencerPosts,
          percent: getPercent(
            pipelineFlow.influencers.savedInfluencerPosts,
            pipelineFlow.influencers.totalInfluencerPosts,
          ),
        },
        {
          num: 8,
          title: "Hashtag Brand RunPod",
          desc: "Hashtag brand postları RunPod analizi",
          status: buildStatus(
            pipelineFlow.hashtagBrands.hashtagBrandPostsRunpodAnalyzed +
              pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis,
            pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis,
          ),
          total:
            pipelineFlow.hashtagBrands.hashtagBrandPostsRunpodAnalyzed +
            pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis,
          completed: pipelineFlow.hashtagBrands.hashtagBrandPostsRunpodAnalyzed,
          waiting: pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis,
          waitingExtra: `${pods.length} pod aktif`,
          percent: getPercent(
            pipelineFlow.hashtagBrands.hashtagBrandPostsRunpodAnalyzed,
            pipelineFlow.hashtagBrands.hashtagBrandPostsRunpodAnalyzed +
              pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis,
          ),
        },
        {
          num: 9,
          title: "GPT Brand Analysis",
          desc: "Hashtag brand postları GPT analizi",
          status: buildStatus(
            pipelineFlow.hashtagBrands.hashtagBrandPostsGptAnalyzed +
              pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis,
            pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis,
          ),
          total:
            pipelineFlow.hashtagBrands.hashtagBrandPostsGptAnalyzed +
            pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis,
          completed: pipelineFlow.hashtagBrands.hashtagBrandPostsGptAnalyzed,
          waiting: pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis,
          percent: getPercent(
            pipelineFlow.hashtagBrands.hashtagBrandPostsGptAnalyzed,
            pipelineFlow.hashtagBrands.hashtagBrandPostsGptAnalyzed +
              pipelineFlow.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis,
          ),
        },
      ]
    : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-pink-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Hashtag Pipeline Durumu</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              fetchPipelineFlow()
              fetchPods()
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition"
          >
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Genel Özet */}
      <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Genel Özet</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            title="Toplam Hashtag Post"
            value={pipelineFlow?.hashtagPostCounts.totalHashtagPosts || 0}
            color="blue"
          />
          <StatCard
            title="RunPod Analizli Hashtag"
            value={pipelineFlow?.hashtagPostCounts.hashtagPostsRunpodAnalyzed || 0}
            color="purple"
            percent={getPercent(
              pipelineFlow?.hashtagPostCounts.hashtagPostsRunpodAnalyzed || 0,
              (pipelineFlow?.hashtagPostCounts.hashtagPostsRunpodAnalyzed || 0) +
                (pipelineFlow?.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis || 0),
            )}
          />
          <StatCard
            title="GPT Analizli Hashtag"
            value={pipelineFlow?.hashtagPostCounts.hashtagPostsGptAnalyzed || 0}
            color="green"
            percent={getPercent(
              pipelineFlow?.hashtagPostCounts.hashtagPostsGptAnalyzed || 0,
              (pipelineFlow?.hashtagPostCounts.hashtagPostsGptAnalyzed || 0) +
                (pipelineFlow?.hashtagPostCounts.hashtagPostsNeedingGptAnalysis || 0),
            )}
          />
          <StatCard
            title="Toplam Influencer Post"
            value={pipelineFlow?.influencers.totalInfluencerPosts || 0}
            color="gray"
          />
          <StatCard
            title="Toplam Brand Post"
            value={pipelineFlow?.hashtagBrands.totalHashtagBrandPosts || 0}
            color="green"
            percent={getPercent(
              pipelineFlow?.hashtagBrands.savedHashtagBrandPosts || 0,
              pipelineFlow?.hashtagBrands.totalHashtagBrandPosts || 0,
            )}
          />
        </div>
      </div>

      {/* Pipeline Adımları Tablosu */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-700">Pipeline Adımları</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-10">
                  #
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Adım
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-28">
                  Durum
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Toplam
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Tamamlanan
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Bekleyen
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-36">
                  Oran
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pipelineTableData.map((row) => (
                <tr key={row.num} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm font-bold text-gray-400">{row.num}</td>
                  <td className="px-3 py-3">
                    <div className="text-sm font-medium text-gray-800">{row.title}</div>
                    <div className="text-xs text-gray-500">{row.desc}</div>
                  </td>
                  <td className="px-3 py-3 text-center">{renderStatusBadge(row.status)}</td>
                  <td className="px-3 py-3 text-right text-sm font-semibold text-gray-700">
                    {fmt(row.total)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-green-700 font-medium">
                    {fmt(row.completed)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="text-sm text-yellow-700 font-medium">{fmt(row.waiting)}</div>
                    {row.waitingExtra && (
                      <div className="text-xs text-gray-500">{row.waitingExtra}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right">
                        {row.percent}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detay Tabloları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {/* Hashtag Post Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Hashtag Post Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.hashtagPostCounts.totalHashtagPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.savedHashtagPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilmeyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.unsavedHashtagPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Tag İçeren</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-purple-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.postsWithTags)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">RunPod Analizli</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.hashtagPostsRunpodAnalyzed)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">RunPod Bekleyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.hashtagPostsNeedingRunpodAnalysis)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* GPU & RunPod Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">GPU & RunPod Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Aktif Pod</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {pods.length}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Aktif)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.runpodWorkers.active)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Çalışan)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.runpodWorkers.running)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Boşta)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  {fmt(pipelineFlow?.runpodWorkers.idle)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Etiketli Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-purple-700">
                  {fmt(pipelineFlow?.hashtagPostCounts.postsWithTags)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Influencer Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Influencer Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Influencer</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.influencers.total)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Influencer Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.influencers.totalInfluencerPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.influencers.savedInfluencerPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilmeyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.influencers.unsavedInfluencerPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kayıt Oranı</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  %{getPercent(
                    pipelineFlow?.influencers.savedInfluencerPosts || 0,
                    pipelineFlow?.influencers.totalInfluencerPosts || 0,
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Hashtag Brand Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Hashtag Brand Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Marka</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.hashtagBrands.total)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Brand Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.hashtagBrands.totalHashtagBrandPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.hashtagBrands.savedHashtagBrandPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilmeyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.hashtagBrands.unsavedHashtagBrandPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">RunPod Bekleyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  {fmt(pipelineFlow?.hashtagBrands.hashtagBrandPostsNeedingRunpodAnalysis)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">GPT Bekleyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  {fmt(pipelineFlow?.hashtagBrands.hashtagBrandPostsNeedingGptAnalysis)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* RunPod Pods Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Aktif RunPod Podları ({pods.length})
            {isLoadingPods && <span className="ml-2 text-sm text-gray-500">(yükleniyor...)</span>}
          </h2>
          {pods.length > 0 && (
            <button
              onClick={handleTerminateAll}
              disabled={terminatingAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg shadow transition"
            >
              {terminatingAll ? "Terminate ediliyor..." : "Tümünü Kapat"}
            </button>
          )}
        </div>

        {pods.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            Aktif pod bulunmuyor
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Pod ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    İsim
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    GPU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Çalışma Süresi
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pods.map((pod) => (
                  <tr key={pod.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{pod.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{pod.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{pod.gpuCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatUptime(pod.uptimeSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleTerminatePod(pod.id)}
                        disabled={terminatingPod === pod.id}
                        className="px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded disabled:opacity-50"
                      >
                        {terminatingPod === pod.id ? "..." : "Kapat"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Last Updated */}
      {pipelineFlow?.lastUpdated && (
        <div className="text-sm text-gray-500 text-right">
          Son güncelleme: {new Date(pipelineFlow.lastUpdated).toLocaleString("tr-TR")}
        </div>
      )}
    </div>
  )
}

export default HashtagPipeline
